import { Fragment, useEffect, useMemo, useState } from 'react'
import { ArrowPathIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import SettingsSection from '@/components/SettingsSection'
import UserPermissionOverrides from '@/components/UserPermissionOverrides'
import { toast } from '@/store/toastStore'
import { useAuth } from '@/contexts/AuthContext'
import {
  getPermissions,
  updatePermissions,
  type PermissionsResponse,
} from '@/services/permissions'
import { groupPermissionPagesByNav } from '@/config/navPages'
import {
  ACCESS_OPTIONS,
  isAdmin,
  ROLE_OPTIONS,
  type PageAccess,
  type UserRole,
} from '@/utils/pagePermissions'

export default function PermissionsSection() {
  const { user } = useAuth()
  const [data, setData] = useState<PermissionsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingCell, setSavingCell] = useState<string | null>(null)

  const loadPermissions = async () => {
    setLoading(true)
    try {
      const response = await getPermissions()
      setData(response)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin(user)) {
      void loadPermissions()
    } else {
      setLoading(false)
    }
  }, [user])

  const pageGroups = useMemo(
    () => (data ? groupPermissionPagesByNav(data.pages) : []),
    [data],
  )
  const roleLabelBySlug = useMemo(
    () => new Map(ROLE_OPTIONS.map(({ value, label }) => [value, label])),
    [],
  )

  if (!isAdmin(user)) {
    return null
  }

  const handleAccessChange = async (role: UserRole, pageKey: string, access: PageAccess) => {
    const cellKey = `${role}:${pageKey}`
    setSavingCell(cellKey)
    try {
      const response = await updatePermissions([{ role, page_key: pageKey, access }])
      setData(response)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update permission')
    } finally {
      setSavingCell(null)
    }
  }

  const sectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100/90 ring-1 ring-sky-200/50">
      <ShieldCheckIcon className="h-5 w-5 text-sky-700" />
    </div>
  )

  const roleColumnCount = data?.roles.length ?? 0

  return (
    <SettingsSection
      title="Page permissions"
      description="Control which roles can edit, view read-only, or hide each page in the sidebar and app navigation. Use user overrides below for exceptions."
      icon={sectionIcon}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading permissions…
        </div>
      ) : data ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-auto divide-y divide-gray-200 text-sm">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3 pr-4 text-left font-semibold text-gray-900"
                  >
                    Page
                  </th>
                  {data.roles.map((role) => (
                    <th
                      key={role}
                      scope="col"
                      className="px-2 py-3 text-left font-semibold capitalize text-gray-900"
                    >
                      {roleLabelBySlug.get(role as UserRole) ?? role}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageGroups.map((group) => (
                  <Fragment key={`section-${group.section}`}>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={roleColumnCount + 1}
                        className="px-0 py-2 pr-4 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
                      >
                        {group.section}
                      </td>
                    </tr>
                    {group.pages.map((page) => (
                      <tr key={page.key}>
                        <td className="py-3 pr-4 font-medium text-gray-900 whitespace-nowrap">
                          {page.label}
                        </td>
                        {data.roles.map((role) => {
                          const cellKey = `${role}:${page.key}`
                          const currentAccess = data.matrix[role]?.[page.key] ?? 'hidden'
                          return (
                            <td key={role} className="px-2 py-3">
                              <select
                                className="input select !w-[7.5rem] text-sm"
                                value={currentAccess}
                                disabled={savingCell === cellKey}
                                onChange={(e) =>
                                  void handleAccessChange(
                                    role,
                                    page.key,
                                    e.target.value as PageAccess,
                                  )
                                }
                              >
                                {ACCESS_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <UserPermissionOverrides data={data} onDataChange={setData} />
        </>
      ) : null}
    </SettingsSection>
  )
}
